<template>
  <div
    v-bind="$attrs"
    class="border border-gray-200/75 rounded-lg p-1.5 flex items-center h-8 gap-x-px cursor-pointer [content-visibility:auto]"
    @mouseleave="clearHover"
  >
    <div
      v-for="(marker, index) in props.markers"
      :key="index"
      class="w-1/128 h-full"
      :class="index === hoveredIndex && 'z-10'"
      @mouseenter="handleEnter($event, marker, index)"
    >
      <div
        :style="{
          ...(marker && {
            background: marker.color,
            opacity: marker.visible === false ? 0.15 : 1,
          }),
          ...(index === hoveredIndex && { transform: 'scaleX(2) scaleY(1.4)' }),
        }"
        :class="marker?.shine && 'isolate'"
        class="pointer-events-none w-full h-full flex items-center transition-transform duration-150 ease-out"
      >
        <div
          v-if="marker?.shine"
          class="w-full h-[90%] mix-blend-overlay opacity-75 bg-[linear-gradient(to_bottom,transparent_0%,#fff_37.5%,#fff_62.5%,transparent_100%)]"
        ></div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <Transition name="marker-tooltip">
      <div
        v-if="hoveredHash"
        ref="tooltip"
        role="tooltip"
        class="pointer-events-none z-[999] flex w-fit items-baseline gap-x-[5px] whitespace-nowrap rounded-lg bg-gray-900 px-[9px] py-[5px] text-[14px] leading-[18px] text-white"
        :style="[floatingStyles, { boxShadow }]"
      >
        <span class="text-white/75">GFM:</span>
        <span>{{ hoveredHash }}</span>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue'
import { ref } from 'vue'

export interface MarkerLike {
  color: string
  shine: boolean
  visible?: boolean | null
  hash?: string | null
}

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  markers: ReadonlyArray<MarkerLike | null>
}>()

const hoveredIndex = ref<number | null>(null)
const hoveredHash = ref<string | null>(null)
const hoveredBar = ref<HTMLElement | null>(null)
const tooltip = ref<HTMLElement>()

const boxShadow = '0 2px 10px rgba(0,0,0,0.1), 0 0 20px rgba(0,0,0,0.05)'

const { floatingStyles } = useFloating(hoveredBar, tooltip, {
  placement: 'top',
  whileElementsMounted: autoUpdate,
  transform: false,
  middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
})

const handleEnter = (
  event: MouseEvent,
  marker: MarkerLike | null,
  index: number
) => {
  if (!marker) return

  hoveredIndex.value = index
  hoveredHash.value = marker.hash || null
  hoveredBar.value = event.currentTarget as HTMLElement
}

const clearHover = () => {
  hoveredIndex.value = null
  hoveredHash.value = null
}
</script>

<style scoped>
.marker-tooltip-enter-active {
  transition:
    opacity 0.1s,
    transform 0.13s cubic-bezier(0.4, 0, 0.2, 1);
}
.marker-tooltip-leave-active {
  transition:
    opacity 0.1s,
    transform 0.1s cubic-bezier(0.4, 0, 0.2, 1);
}
.marker-tooltip-enter-from,
.marker-tooltip-leave-to {
  opacity: 0;
  transform: translate(0, 6px);
}
</style>
